import { useState } from "react";
import { H3, Label } from "../Text";
import { toast } from "react-toastify";
import Circle from "../Animation/Circle";
import { Button } from "../Buttons";
import { Request } from "../../hooks/Request";
import TableComplete from "../Tables/TableComplete";
import { requestAuth } from "../services/RequestService";
import { useDialog } from "../../contexts/DialogContext";
import { DialogConfirmation } from "../Modal";
import CreatableSelect from "react-select/creatable";
import { numberWithDecimals, validateArray } from "../../utils/validate";
import * as XLSX from "xlsx";

import { OrderPreviewModal } from "../../containers/steps/OrderPreviewModal";
import {
  DEFAULT_INSURANCE_MATERIAL_TYPE,
  getInsuranceRule,
  INSURANCE_MATERIAL_UPDATED_EVENT,
} from "../../utils/insuranceRules";

const normalizeCompareText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

const shouldShowSecondaryDetail = (primary, secondary) => {
  if (!secondary) return false;

  return normalizeCompareText(primary) !== normalizeCompareText(secondary);
};

const safeJsonParse = (value, fallback = {}) => {
  try {
    if (!value) return fallback;
    return typeof value === "string" ? JSON.parse(value) : value;
  } catch {
    return fallback;
  }
};

const getCurrentInsuranceMaterialType = (rows = []) => {
  const firstRowWithType = rows.find((row) => {
    const extra = safeJsonParse(row.description2);
    return extra?.insuranceMaterialType;
  });

  if (!firstRowWithType) return DEFAULT_INSURANCE_MATERIAL_TYPE;

  return (
    safeJsonParse(firstRowWithType.description2)?.insuranceMaterialType ||
    DEFAULT_INSURANCE_MATERIAL_TYPE
  );
};

const buildPreviewItemsFromSubData = (rows = []) =>
  rows.map((row, index) => {
    const extra = safeJsonParse(row.description2);

    return {
      subDataId: row.id,
      itemNo: row.numberValue || index + 1,
      desc: extra.desc || row.label || "",
      unit: extra.unit || "",
      amount: extra.amount || "",
      unitPrice: extra.unitPrice || "",
      discount: extra.discount || "",
      itemTotal: extra.itemTotal || "",
      delivery: extra.delivery || "",
      tr4: extra.tr4 || "",
      providerCode: extra.providerCode || "",
    };
  });


const InputSubDataBuy = ({ field }) => {
  const { openDialog, dialogClose } = useDialog();

  const { data, loading, call } = Request({
    urlApi: `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
  });

  const {
    data: document,
    loading: loadDocument,
    call: callDocument,
  } = Request({
    urlApi: field.urlApi,
  });

  function handleForm(item) {
    const values = item.description2
      ? JSON.parse(item.description2) || null
      : null;
    openDialog(
      <>
        {loadDocument && (
          <FormAddDocument
            data={document}
            item={{
              ...item,
              desc: values?.desc || "",
              amount: values?.amount || "",
              unit: values?.unit || "",
              tr4: values?.tr4 || "",
              providerCode: values?.providerCode || "",
              description2: values?.Description2 || "",
            }}
            reload={call}
            field={field}
            onClose={dialogClose}
            callDocument={callDocument}
          />
        )}
        <br />
      </>,
    );
  }

  function handleDelete(data) {
    openDialog(
      <DialogConfirmation
        children={<H3>¿Estas seguro que quieres eliminar este item?</H3>}
        onClose={dialogClose}
        method="delete"
        url={`/dataSet/procedure/subData/${data.id}`}
        texBtn="Eliminar"
        handleSuccess={call}
      />,
    );
  }

  async function handleDeleteAll() {
    const ConfirmContent = () => (
      <div>
        <H3 className="text-center p-4">
          ¿Estás seguro de que quieres eliminar todos los ítems de esta lista?
          Esta acción no se puede deshacer.
        </H3>
        <div className="flex gap-4 m-4">
          <Button
            onClick={dialogClose}
            className="w-full bg-[#fff] text-[#FBCE00] border-2 border-[#FBCE00]"
          >
            Cancelar
          </Button>
          <Button
            onClick={async () => {
              try {
                toast.info("Eliminando ítems...");
                await Promise.all(
                  data.map((item) =>
                    requestAuth(
                      "delete",
                      `/dataSet/procedure/subData/${item.id}`,
                    ),
                  ),
                );
                toast.success("Se eliminaron todos los ítems");
                call();
                dialogClose();
              } catch (err) {
                toast.error("No se pudieron eliminar todos los ítems");
              }
            }}
            className="w-full"
          >
            Eliminar todo
          </Button>
        </div>
      </div>
    );

    openDialog(<ConfirmContent />);
  }

  async function handleOpenInsurancePreview() {
    const currentInsuranceMaterialType = getCurrentInsuranceMaterialType(data);
    const previewItems = buildPreviewItemsFromSubData(data);

    openDialog(
      <OrderPreviewModal
        title="Resumen del Seguro"
        confirmText="Guardar clasificación"
        extractedData={{
          cabecera: [],
          items: previewItems,
          cargoRows: [],
          insuranceMaterialType: currentInsuranceMaterialType,
          procedureId: field.id,
        }}
        onClose={dialogClose}
        onConfirm={async ({ items, insuranceMaterialType }) => {
          const insuranceRule = getInsuranceRule(insuranceMaterialType);

          try {
            await Promise.all(
              items.map((item) =>
                requestAuth(
                  "put",
                  `/dataSet/procedure/subData/${item.subDataId}`,
                  {
                    Label: String(item.desc),
                    NumberValue: Number(item.itemNo) || 0,
                    Description: "",
                    Description2: JSON.stringify({
                      unit: String(item.unit || ""),
                      amount: String(item.amount || ""),
                      desc: String(item.desc || ""),
                      discount: String(item.discount || ""),
                      delivery: String(item.delivery || ""),
                      unitPrice: String(item.unitPrice || ""),
                      itemTotal: String(item.itemTotal || ""),
                      insuranceMaterialType: insuranceRule.value,
                      insuranceFactor: String(insuranceRule.factor),
                      insuranceRate: String(insuranceRule.ratePercent),
                      insuranceLabel: insuranceRule.label,
                      tr4: String(item.tr4 || ""),
                      providerCode: String(item.providerCode || ""),
                    }),
                    unit: String(item.unit || ""),
                    amount: String(item.amount || ""),
                    desc: String(item.desc || ""),
                    discount: String(item.discount || ""),
                    delivery: String(item.delivery || ""),
                    tr4: String(item.tr4 || ""),
                    providerCode: String(item.providerCode || ""),
                    isNew: false,
                  },
                ),
              ),
            );

            toast.success("Clasificación de seguro actualizada");

            call();

            if (typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent(INSURANCE_MATERIAL_UPDATED_EVENT, {
                  detail: {
                    procedureId: field?.id,
                  },
                }),
              );
            }

            dialogClose();
          } catch (error) {
            toast.error("No se pudo actualizar la clasificación de seguro");
          }
        }}
      />,
    );
  }

  return (
    <div className="bg-gray-300/40 p-2 rounded-md">
      <div className="flex justify-between items-center">
        <Label htmlFor={field.name} className={`${field.className} my-0`}>
          {field.labelCustom ? field.labelCustom : field.label}
        </Label>
        {validateArray(data) && data.length > 0 && (
          <div className="flex items-center gap-2">
            {field.name === "itemsOrdenDeCompra" && (
              <Button
                onClick={handleOpenInsurancePreview}
                className="bg-blue-600 text-white hover:bg-blue-700 px-3 py-1 text-sm font-medium"
              >
                Calculo de tasa
              </Button>
            )}

            <Button
              onClick={handleDeleteAll}
              className="text-red-500 bg-transparent hover:text-red-700 px-3 py-1 text-sm font-medium shadow-none"
            >
              Limpiar ítems
            </Button>
          </div>
        )}
      </div>
      <br />
      {!loading ? (
        <Circle />
      ) : (
        <div className="w-full">
          <TableComplete
            header={[
              {
                name: "index",
                label: "#",
              },
              {
                name: "label",
                label: "Detalle",
                type: "custom",
                builder: (value, _, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2) || null
                    : null;

                  const secondaryDetail = values?.desc || "";

                  return (
                    <div className="">
                      <h3>{value}</h3>

                      {shouldShowSecondaryDetail(value, secondaryDetail) && (
                        <h3 className="font-normal">{secondaryDetail}</h3>
                      )}
                    </div>
                  );
                },
              },
              {
                name: "description",
                label: "Unidad",
                type: "custom",
                builder: (_, __, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2) || null
                    : null;
                  return (
                    <div className="">
                      <h3 className="font-normal">{values?.unit || "-"}</h3>
                    </div>
                  );
                },
              },
              {
                name: "description",
                label: "Cantidad / Precio",
                type: "custom",
                builder: (_, __, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2) || null
                    : null;
                  return (
                    <div className="">
                      <h3 className="font-normal">
                        {numberWithDecimals(Number(values?.amount)) || "-"}
                      </h3>
                      <h3 className="font-normal">
                        {numberWithDecimals(Number(values?.unitPrice)) || "-"}
                      </h3>
                    </div>
                  );
                },
              },
              /* {
                name: "discount",
                label: "Descuento",
                type: "custom",
                builder: (_, __, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2) || null
                    : null;
                  return (
                    <div className="">
                      <h3 className="font-normal">
                        {numberWithDecimals(Number(values?.discount)) || "-"}
                      </h3>
                    </div>
                  );
                },
              }, */
              {
                name: "delivery",
                label: "Total",
                type: "custom",
                builder: (_, __, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2) || null
                    : null;
                  return (
                    <div className="">
                      <h3 className="font-bold">
                        {numberWithDecimals(
                          Number(values?.amount || 0) *
                            Number(values?.unitPrice || 0) -
                            Number(values?.discount || 0),
                        ) || "-"}
                      </h3>
                      <h3 className="font-normal">{values?.delivery || "-"}</h3>
                    </div>
                  );
                },
              },
              {
                name: "add",
                type: "action",
                label: "Opciones",
                sticky: true,
                actions: [
                  {
                    label: "Eliminar",
                    icon: "fas fa-trash",
                    action: handleDelete,
                    color: "text-red-500",
                  },
                ],
              },
            ]}
            data={data?.map((item, i) => ({ ...item, index: i + 1 }))}
          />
        </div>
      )}
    </div>
  );
};

const FormAddDocument = (props) => {
  const { data, item = null, reload, field, onClose, callDocument } = props;
  const [values, setValues] = useState({
    Label: item?.label ? item.label : "",
    NumberValue: item?.numberValue ? item.numberValue : 0,
    Description: item?.description ? item.description : "",
    Description2: item?.description2 ? item.description2 : "",
    unit: item?.unit ? item.unit : "",
    amount: item?.amount ? item.amount : "",
    desc: item?.desc ? item.desc : "",
    tr4: item?.tr4 ? item.tr4 : "",
    providerCode: item?.providerCode ? item.providerCode : "",
    isNew: false,
  });

  async function handleSubmit() {
    if (values.isNew) {
      await handleCreateOption(values);
    }
    const desc2 = {
      unit: values.unit || 0,
      amount: values.amount || 0,
      desc: values.desc || 0,
      tr4: values.tr4 || "",
      providerCode: values.providerCode || "",
    };
    await requestAuth(
      item ? "put" : "post",
      item
        ? `/dataSet/procedure/subData/${item.id}`
        : `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
      { ...values, Description2: JSON.stringify(desc2) },
    )
      .then(() => {
        reload();
        onClose();
        setValues({
          ...values,
          NumberValue: 0,
          Description: "",
          unit: "",
          amount: "",
          desc: "",
          tr4: "",
          providerCode: "",
        });
        toast.success("Registro exitoso");
      })
      .catch(() => {
        toast.error("Se produjo un error al guardar");
      });
  }

  async function handleCreateOption(value) {
    await requestAuth("post", "/Dropdown/Option", {
      Name: value.Label,
      DropdownListLabel: field.urlInitial,
    }).then(() => {
      callDocument();
    });
  }

  function handleChangeInp(e) {
    const { name, value } = e.target;
    setValues((a) => ({
      ...a,
      [name]: value,
    }));
  }
  function handleChange(e) {
    setValues((a) => ({
      ...a,
      Label: e?.value || "",
      isNew: e?.__isNew__ || false,
    }));
  }

  return (
    <form className="flex flex-col">
      <Label className={`mt-2`}>Detalle</Label>
      {validateArray(data) ? (
        <CreatableSelect
          isSearchable={true}
          onChange={handleChange}
          styles={{
            placeholder: (base) => ({
              ...base,
              color: "black",
            }),
          }}
          defaultInputValue={values.Label}
          placeholder={"Seleccionar..."}
          isOptionSelected={false}
          isClearable
          options={data.map((option) => ({
            value: option.name,
            label: option.name,
          }))}
        />
      ) : null}
      <div className="flex gap-1 w-full">
        <div className="w-1/2">
          <Label className={`my-0 mt-4`}>Cod. TR4</Label>
          <input
            value={values.tr4}
            onChange={handleChangeInp}
            className="w-full p-2 rounded-md"
            type="text"
            name="tr4"
          />
        </div>
        <div className="w-1/2">
          <Label className={`my-0 mt-4`}>Cod. del proveedor</Label>
          <input
            value={values.providerCode}
            onChange={handleChangeInp}
            className="w-full p-2 rounded-md"
            type="text"
            name="providerCode"
          />
        </div>
      </div>
      <div className="flex gap-1 items-end">
        <div className="">
          <Label className={`my-0 mt-4`}>Unidad</Label>
          <input
            value={values.unit}
            onChange={handleChangeInp}
            className="w-full p-2 rounded-md"
            type="text"
            name="unit"
          />
        </div>{" "}
        <div className="">
          <Label className={`my-0 mt-4`}>Cantidad</Label>
          <input
            value={values.amount}
            onChange={handleChangeInp}
            className="w-full p-2 rounded-md"
            type="text"
            name="amount"
          />
        </div>{" "}
        <div className="">
          <Label className={`my-0 mt-4`}>Obs.</Label>
          <input
            value={values.desc}
            onChange={handleChangeInp}
            className="w-full p-2 rounded-md"
            type="text"
            name="desc"
          />
        </div>
        <Button onClick={handleSubmit} className="px-3 py-2">
          Guardar
        </Button>
      </div>
    </form>
  );
};

export default InputSubDataBuy;
